/**
 * supabase.js — Frontend Supabase Client Configuration
 * For authentication and real-time features in React components
 */
import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables
// Supports both NEXT_PUBLIC_ (recommended) and VITE_ prefixes
const supabaseUrl = import.meta.env.NEXT_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️  Supabase credentials not found. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY in your .env file');
}

// Create Supabase client for browser/frontend use
export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
    })
  : null;

// Helper functions for authentication

/**
 * Sign up a new user
 */
export async function signUp(email, password, metadata = {}) {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
    },
  });

  if (error) throw error;
  return data;
}

/**
 * Sign in with email and password
 */
export async function signIn(email, password) {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

/**
 * Sign out current user
 */
export async function signOut() {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get current session
 */
export async function getSession() {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
}

/**
 * Get current user
 */
export async function getCurrentUser() {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(callback) {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return subscription;
}

/**
 * Refresh session
 */
export async function refreshSession() {
  if (!supabase) throw new Error('Supabase not initialized');
  
  const { data, error } = await supabase.auth.refreshSession();
  if (error) throw error;
  return data;
}

export default supabase;
